import { useMemo } from 'react';
import type { IssueCode, OutcomeCode } from '../api/types';

/** Groups the ASIC issue codes into their categories, preserving server order. */
function groupByCategory(issues: IssueCode[]) {
  const groups = new Map<string, IssueCode[]>();
  for (const issue of issues) {
    if (issue.code === 999) continue;
    const bucket = groups.get(issue.category);
    if (bucket) bucket.push(issue);
    else groups.set(issue.category, [issue]);
  }
  return [...groups.entries()];
}

interface IssueSelectProps {
  id: string;
  issues: IssueCode[];
  value: number;
  disabled?: boolean;
  onChange: (code: number) => void;
}

export function IssueSelect({ id, issues, value, disabled, onChange }: IssueSelectProps) {
  const groups = useMemo(() => groupByCategory(issues), [issues]);

  return (
    <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))}>
      <option value={999}>Not applicable</option>
      {groups.map(([category, codes]) => (
        <optgroup key={category} label={category}>
          {codes.map((issue) => (
            <option key={issue.code} value={issue.code}>
              [{issue.code}] {issue.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

interface OutcomeSelectProps {
  id: string;
  outcomes: OutcomeCode[];
  value: number;
  disabled?: boolean;
  onChange: (code: number) => void;
}

export function OutcomeSelect({ id, outcomes, value, disabled, onChange }: OutcomeSelectProps) {
  return (
    <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))}>
      {outcomes.map((outcome) => (
        <option key={outcome.code} value={outcome.code}>
          {outcome.code === 999 ? outcome.label : `${outcome.code} — ${outcome.label}`}
        </option>
      ))}
    </select>
  );
}

/** Renders an issue code as "[7] Fees/costs" for read-only display. */
export function issueLabel(issues: IssueCode[], code: number): string {
  if (code === 999) return 'Not applicable';
  const match = issues.find((i) => i.code === code);
  return match ? `[${match.code}] ${match.label}` : String(code);
}

export function outcomeLabel(outcomes: OutcomeCode[], code: number): string {
  return outcomes.find((o) => o.code === code)?.label ?? String(code);
}
